import React, { useState, useEffect, useCallback } from 'react';
import { searchTRUDMedications, assignMedicationToUser } from '../../services/medicationService';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const AssignMedicationModal = ({ serverId, onClose, onSuccess }) => {
    const { user, userDetails } = useAuth();
    
    // Steps: 1 = Search, 2 = Select Users, 3 = Settings (Times/Quantities)
    const [step, setStep] = useState(1);
    
    // Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    
    // User Selection State
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    
    // Settings State
    const [timings, setTimings] = useState(['08:00']);
    const [requiredQuantity, setRequiredQuantity] = useState(30);
    const [isControlled, setIsControlled] = useState(false);
    const [route, setRoute] = useState('Oral');
    
    const [isSaving, setIsSaving] = useState(false);

    // Fetch users for step 2
    useEffect(() => {
        const fetchUsers = async () => {
            setIsLoadingUsers(true);
            try {
                const usersRef = collection(db, 'servers', serverId, 'serviceUsers');
                const q = query(usersRef, where('status', '==', true));
                const snap = await getDocs(q);
                const users = [];
                snap.forEach(d => users.push({ id: d.id, ...d.data() }));
                setAvailableUsers(users);
            } catch (err) {
                console.error("Failed to fetch users", err);
            } finally {
                setIsLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [serverId]);

    // Debounced TRUD Search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.length >= 3) {
                executeSearch(searchQuery);
            } else {
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const executeSearch = async (term) => {
        setIsSearching(true);
        try {
            const results = await searchTRUDMedications(term);
            setSearchResults(results);
        } catch (err) {
            console.error("TRUD Search Error", err);
            // Optionally set fallback or error message
        } finally {
            setIsSearching(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const adminId = user?.uid;
            const adminName = userDetails?.firstName ? `${userDetails.firstName} ${userDetails.lastName}` : user?.email;
            
            const medData = {
                ...selectedMedicine,
                timings,
                requiredQuantity,
                isControlled,
                route
            };
            
            // Replicate batch save for each user
            for (const u of selectedUsers) {
                await assignMedicationToUser(serverId, u.id, u.fullName || u.name, adminId, adminName, medData);
            }
            
            onSuccess();
        } catch (err) {
            console.error("Failed to assign medication", err);
            alert("Failed to assign: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const toggleUser = (u) => {
        if (selectedUsers.find(su => su.id === u.id)) {
            setSelectedUsers(selectedUsers.filter(su => su.id !== u.id));
        } else {
            setSelectedUsers([...selectedUsers, u]);
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '800px', maxWidth: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                
                {/* Header */}
                <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, color: '#2d3748', fontSize: '20px' }}>
                        {step === 1 ? 'Search TRUD Library' : step === 2 ? 'Select Users' : 'Medication Settings'}
                    </h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer', color: '#718096' }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                    {/* STEP 1: TRUD SEARCH */}
                    {step === 1 && (
                        <div>
                            <input 
                                type="text"
                                placeholder="Search by medication name, brand, or TRUD ID (min 3 chars)..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '16px', marginBottom: '15px' }}
                                autoFocus
                            />
                            
                            {isSearching ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Searching TRUD Database...</div>
                            ) : searchResults.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {searchResults.map((med, idx) => (
                                        <div 
                                            key={idx}
                                            onClick={() => {
                                                setSelectedMedicine(med);
                                                setStep(2);
                                            }}
                                            style={{ 
                                                padding: '12px', 
                                                border: '1px solid #e2e8f0', 
                                                borderRadius: '6px', 
                                                cursor: 'pointer',
                                                backgroundColor: selectedMedicine?.id === med.id ? '#ebf8ff' : 'white',
                                                borderColor: selectedMedicine?.id === med.id ? '#3182ce' : '#e2e8f0'
                                            }}
                                        >
                                            <div style={{ fontWeight: 'bold', color: '#2d3748' }}>{med.med_name}</div>
                                            <div style={{ fontSize: '13px', color: '#718096', display: 'flex', gap: '15px', marginTop: '4px' }}>
                                                {med.type && <span>Type: {med.type}</span>}
                                                {med.dosage && <span>Dose: {med.dosage}</span>}
                                                {med.unit && <span>Form: {med.unit}</span>}
                                                {med.manufacturer && <span>Mfr: {med.manufacturer}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : searchQuery.length >= 3 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>No medications found. Try a different search term.</div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Enter at least 3 characters to search the TRUD database.</div>
                            )}
                        </div>
                    )}

                    {/* STEP 2: SELECT USERS */}
                    {step === 2 && (
                        <div>
                            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#ebf8ff', borderRadius: '6px', color: '#2b6cb0' }}>
                                <strong>Selected Medication:</strong> {selectedMedicine?.med_name}
                            </div>
                            
                            <h3 style={{ fontSize: '16px', color: '#4a5568', marginBottom: '10px' }}>Select Users to Assign To:</h3>
                            
                            {isLoadingUsers ? (
                                <div>Loading users...</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {availableUsers.map(u => {
                                        const isSelected = !!selectedUsers.find(su => su.id === u.id);
                                        return (
                                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', backgroundColor: isSelected ? '#ebf8ff' : 'white' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={isSelected}
                                                    onChange={() => toggleUser(u)}
                                                    style={{ marginRight: '15px', transform: 'scale(1.2)' }}
                                                />
                                                <div style={{ fontWeight: '500', color: '#2d3748' }}>{u.fullName || u.name || 'Unnamed User'}</div>
                                            </label>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3: SETTINGS */}
                    {step === 3 && (
                        <div>
                            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#ebf8ff', borderRadius: '6px', color: '#2b6cb0' }}>
                                Assigning <strong>{selectedMedicine?.med_name}</strong> to <strong>{selectedUsers.length}</strong> user(s).
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Timings (HH:MM)</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        {timings.map((t, idx) => (
                                            <input 
                                                key={idx}
                                                type="time" 
                                                value={t}
                                                onChange={(e) => {
                                                    const newT = [...timings];
                                                    newT[idx] = e.target.value;
                                                    setTimings(newT);
                                                }}
                                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                                            />
                                        ))}
                                        <button 
                                            onClick={() => setTimings([...timings, '12:00'])}
                                            style={{ padding: '8px 12px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                        >+ Add Time</button>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Total Quantity Prescribed</label>
                                    <input 
                                        type="number" 
                                        value={requiredQuantity}
                                        onChange={(e) => setRequiredQuantity(parseInt(e.target.value, 10) || 0)}
                                        style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%' }}
                                    />
                                </div>
                                
                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Route of Administration</label>
                                    <select 
                                        value={route}
                                        onChange={(e) => setRoute(e.target.value)}
                                        style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', backgroundColor: 'white' }}
                                    >
                                        <option value="Oral">Oral</option>
                                        <option value="Topical">Topical</option>
                                        <option value="Inhalation">Inhalation</option>
                                        <option value="Transdermal">Transdermal Patch</option>
                                        <option value="Subcutaneous">Subcutaneous (Injection)</option>
                                        <option value="Intramuscular">Intramuscular</option>
                                        <option value="Ophthalmic">Ophthalmic (Eye)</option>
                                        <option value="Otic">Otic (Ear)</option>
                                        <option value="Rectal">Rectal</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', fontWeight: '600', color: '#e53e3e', cursor: 'pointer' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={isControlled}
                                            onChange={(e) => setIsControlled(e.target.checked)}
                                            style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                                        />
                                        This is a Controlled Drug (CD)
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', backgroundColor: '#f7fafc' }}>
                    <button 
                        onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                        style={{ padding: '10px 20px', border: 'none', backgroundColor: 'transparent', color: '#4a5568', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    
                    {step === 1 && (
                        <button 
                            disabled={!selectedMedicine}
                            onClick={() => setStep(2)}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: selectedMedicine ? '#3182ce' : '#cbd5e0', color: 'white', fontWeight: 'bold', cursor: selectedMedicine ? 'pointer' : 'not-allowed' }}
                        >
                            Next: Select Users
                        </button>
                    )}
                    
                    {step === 2 && (
                        <button 
                            disabled={selectedUsers.length === 0}
                            onClick={() => setStep(3)}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: selectedUsers.length > 0 ? '#3182ce' : '#cbd5e0', color: 'white', fontWeight: 'bold', cursor: selectedUsers.length > 0 ? 'pointer' : 'not-allowed' }}
                        >
                            Next: Settings
                        </button>
                    )}
                    
                    {step === 3 && (
                        <button 
                            disabled={isSaving}
                            onClick={handleSave}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#38a169', color: 'white', fontWeight: 'bold', cursor: isSaving ? 'not-allowed' : 'pointer' }}
                        >
                            {isSaving ? 'Saving...' : 'Assign Medication'}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AssignMedicationModal;
